"use client";

import { Share2, X } from "lucide-react";
import posthog from "posthog-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { renderDiscoverNoteSource } from "@/discover/sources/client/discover-client-source-registry";
import { useExploreRoute } from "../../_lib/explore-route-context";
import type { ExploreBriefCard, ExploreBriefEvidenceRef } from "../../types";
import type { BriefOpenMethod, BriefSurface } from "../use-brief-analytics";

interface BriefPreviewDialogProps {
  brief: ExploreBriefCard | null;
  categoryBriefs?: ExploreBriefCard[];
  onNavigateBrief?: (brief: ExploreBriefCard) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Surface this preview was opened from. Threaded into brief_opened / brief_shared. */
  surface?: BriefSurface;
  positionInList?: number | null;
  /** How the dialog was opened — "deep_link" when driven by ?brief= URL params. */
  openMethod?: BriefOpenMethod;
}

function NoteSourceEmbed({
  exploreBase,
  note,
  onOpenChange,
}: {
  exploreBase: string;
  note: ExploreBriefEvidenceRef;
  onOpenChange: (open: boolean) => void;
}) {
  return renderDiscoverNoteSource({ exploreBase, note, onOpenChange });
}

// ─── Share button ───────────────────────────────────────────

function ShareButton({
  brief,
  edition,
  surface,
  positionInList,
}: {
  brief: ExploreBriefCard;
  edition: "engineer" | "creative";
  surface: BriefSurface;
  positionInList: number | null;
}) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    const shareUrl = new URL(window.location.href);
    shareUrl.searchParams.set("brief", brief.briefId);
    // briefIds are only unique per report date — date the link so the
    // deep-link consumer never opens a different day's reused slug.
    shareUrl.searchParams.set("bd", brief.reportDate);

    const baseProps = {
      edition,
      surface,
      brief_id: brief.briefId,
      brief_type: brief.briefType,
      brief_category: brief.categorySlug,
      brief_report_date: brief.reportDate,
      brief_position_in_list: positionInList,
    };

    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: "Brief",
          text:
            brief.title.length > 120
              ? `${brief.title.slice(0, 117)}…`
              : brief.title,
          url: shareUrl.toString(),
        });
        posthog.capture("brief_shared", { ...baseProps, method: "native" });
      } catch {
        // User cancelled — don't count as shared
      }
    } else {
      await navigator.clipboard.writeText(shareUrl.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      posthog.capture("brief_shared", { ...baseProps, method: "copy_link" });
    }
  }, [brief, edition, surface, positionInList]);

  return (
    <button
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--explore-text-muted)] transition-colors hover:bg-[var(--explore-border-light)] hover:text-[var(--explore-text-primary)]"
      onClick={handleShare}
      title={copied ? "Copied!" : "Share"}
      type="button"
    >
      {copied ? (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path
            d="M5 13l4 4L19 7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <Share2 className="h-4 w-4" />
      )}
    </button>
  );
}

// ─── Brief content ──────────────────────────────────────────

function BriefPreviewContent({
  brief,
  edition,
  surface,
  positionInList,
  exploreBase,
}: {
  brief: ExploreBriefCard;
  edition: "engineer" | "creative";
  surface: BriefSurface;
  positionInList: number | null;
  exploreBase: string;
}) {
  const evidence =
    brief.evidence.length > 0
      ? brief.evidence
      : [{ sourceType: brief.sourceType, sourceId: brief.sourceId }];

  return (
    <div className="flex flex-col gap-4">
      {brief.categoryDisplayName && (
        <div className="flex items-center gap-2">
          <span className="text-[0.85rem]">{brief.categoryEmoji}</span>
          <span className="font-medium text-[0.75rem] text-[var(--explore-text-muted)]">
            {brief.categoryDisplayName}
          </span>
          <span className="text-[0.68rem] text-[var(--explore-text-muted)]">
            · {brief.timeAgo}
          </span>
        </div>
      )}

      <h3
        className="font-semibold text-[1.1rem] text-[var(--explore-text-primary)] leading-[1.35]"
        style={{ fontFamily: "var(--explore-card-title-stack)" }}
      >
        {brief.title}
      </h3>

      {brief.tldr && (
        <p
          className="rounded-lg bg-[var(--explore-bg-tldr)] px-4 py-3 text-[0.88rem] text-[var(--explore-text-secondary)] leading-[1.6]"
          style={{ fontFamily: "var(--reader-body-font-stack)" }}
        >
          {brief.tldr}
        </p>
      )}

      {/* Evidence (real tweets/sources) */}
      <div className="flex flex-col gap-2 overflow-hidden">
        {evidence.map((ev, i) => (
          <div
            className="min-w-0 max-w-full overflow-hidden"
            key={`${ev.sourceType}:${ev.sourceId}-${i}`}
          >
            <NoteSourceEmbed
              exploreBase={exploreBase}
              note={ev}
              onOpenChange={() => {
                // no-op: evidence cards don't control dialog state
              }}
            />
          </div>
        ))}
      </div>

      {brief.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {brief.tags.map((tag) => (
            <span
              className="rounded-full bg-[var(--explore-tag-bg)] px-2.5 py-1 font-medium text-[0.75rem] text-[var(--explore-tag-text)]"
              key={tag.slug}
            >
              {tag.displayName}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-end border-[var(--explore-border-light)] border-t pt-3">
        <ShareButton
          brief={brief}
          edition={edition}
          positionInList={positionInList}
          surface={surface}
        />
      </div>
    </div>
  );
}

// ─── Portal-based overlay ───────────────────────────────────

export function BriefPreviewDialog({
  brief,
  categoryBriefs,
  onNavigateBrief,
  open,
  onOpenChange,
  surface = "explore_briefs_section",
  positionInList = null,
  openMethod = "click",
}: BriefPreviewDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const { exploreBase, reportType: edition } = useExploreRoute();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!(open && brief)) {
      return;
    }
    posthog.capture("brief_opened", {
      edition,
      surface,
      brief_id: brief.briefId,
      brief_type: brief.briefType,
      brief_category: brief.categorySlug,
      brief_report_date: brief.reportDate,
      brief_position_in_list: positionInList,
      open_method: openMethod,
    });
  }, [brief, edition, open, surface, positionInList, openMethod]);

  // Lock body scroll
  useEffect(() => {
    if (!open) {
      return;
    }
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // Keep keyboard navigation owned by the dialog after clicks inside embeds.
  useEffect(() => {
    if (!open) {
      return;
    }

    const focusDialog = () => {
      dialogRef.current?.focus();
    };

    focusDialog();
    const handlePointerUp = () => {
      window.setTimeout(focusDialog, 50);
    };

    document.addEventListener("pointerup", handlePointerUp);
    return () => document.removeEventListener("pointerup", handlePointerUp);
  }, [open]);

  // Close on Escape and navigate between nearby briefs with ArrowUp/ArrowDown.
  useEffect(() => {
    if (!(open && brief)) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onOpenChange(false);
        return;
      }

      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (!(onNavigateBrief && categoryBriefs) || categoryBriefs.length <= 1) {
        return;
      }

      const activeIndex = categoryBriefs.findIndex(
        (candidate) => candidate.briefId === brief.briefId
      );
      if (activeIndex === -1) {
        return;
      }

      const nextIndex =
        e.key === "ArrowUp"
          ? Math.max(0, activeIndex - 1)
          : Math.min(categoryBriefs.length - 1, activeIndex + 1);

      if (nextIndex !== activeIndex) {
        onNavigateBrief(categoryBriefs[nextIndex]);
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [brief, categoryBriefs, onNavigateBrief, open, onOpenChange]);

  if (!(brief && open && mounted)) {
    return null;
  }

  // Render via portal to <body> — bypasses any parent transform
  return createPortal(
    <div
      className="max-sm:!items-end"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={() => onOpenChange(false)}
        role="presentation"
        style={{
          position: "absolute",
          inset: 0,
          background: "var(--explore-overlay-bg)",
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Dialog — bottom sheet on mobile, centered on desktop */}
      <div
        className="max-sm:!bottom-0 max-sm:!top-auto max-sm:!w-full max-sm:!max-h-[92vh] max-sm:!rounded-b-none max-sm:!rounded-t-2xl"
        ref={dialogRef}
        role="dialog"
        style={{
          position: "relative",
          width: "min(480px, calc(100vw - 2rem))",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "1rem",
          backgroundColor: "var(--explore-bg-card)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          fontFamily: "var(--homepage-sans-stack)",
        }}
        tabIndex={-1}
      >
        {/* Close */}
        <div className="flex justify-end px-5 pt-4 pb-2 max-sm:px-6 max-sm:pt-5">
          <button
            onClick={() => onOpenChange(false)}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "none",
              background: "var(--explore-bg-highlight)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            type="button"
          >
            <X color="var(--explore-text-secondary)" size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-5 pb-5 max-sm:px-6 max-sm:pb-8">
          <BriefPreviewContent
            brief={brief}
            edition={edition}
            exploreBase={exploreBase}
            positionInList={positionInList}
            surface={surface}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
